import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Spinner, makeStyles, shorthands, tokens, useRestoreFocusSource } from "@fluentui/react-components";

import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";

import { useCurrentTenantQuery } from "../hooks/useTenantQueries";
import TenantEditor from "./TenantEditor";
import Logo from "./Logo";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridAutoRows: "max-content",
    gridRowGap: tokens.spacingVerticalL,
    maxWidth: "720px",
    width: "100%",
  },
  card: {
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding("16px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    display: "grid",
    gridRowGap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  logo: {
    width: "64px",
    height: "64px",
    objectFit: "cover",
    backgroundColor: "transparent",
  },
  placeholderLogo: {
    width: "64px",
    height: "64px",
    display: "grid",
    placeItems: "center",
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: 600,
  },
  valueGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
    "@media (max-width: 720px)": {
      gridTemplateColumns: "1fr",
    },
  },
  valueItem: {
    display: "grid",
    gridRowGap: "4px",
  },
  valueLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  valueText: {
    minHeight: "22px",
    color: tokens.colorNeutralForeground1,
    wordBreak: "break-word",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
});

function Tenant() {
  const styles = useStyles();
  const { t } = useTranslation();
  const restoreFocusSourceAttributes = useRestoreFocusSource();

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const { data: currentTenant, error, isLoading, refetch } = useCurrentTenantQuery();

  useEffect(() => {
    if (!error) return;
    setErrors((prev) => [...prev, { message: error?.message || t("generic.error"), type: "error", details: error }]);
  }, [error, t]);

  const isBusy = isLoading;
  const displayValue = (value) => value || t("generic.none", { defaultValue: "None" });

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />

        <div className={styles.header}>
          <Logo
            logoUrl={currentTenant?.logo_ath || currentTenant?.logo_path || null}
            alt={t("generic.logo", "Logo")}
            className={styles.logo}
            fallback={<div className={styles.placeholderLogo}>Logo</div>}
          />
          <div className={styles.headerText}>
            <span className={styles.title}>{currentTenant?.name || t("generic.loading")}</span>
            <span className={styles.subtitle}>{currentTenant?.slug || t("generic.tenant", "Tenant")}</span>
          </div>
        </div>

        <div className={styles.valueGrid}>
          <div className={styles.valueItem}>
            <span className={styles.valueLabel}>{t("generic.name")}</span>
            <span className={styles.valueText}>{displayValue(currentTenant?.name)}</span>
          </div>
          <div className={styles.valueItem}>
            <span className={styles.valueLabel}>{t("generic.slug", "Slug")}</span>
            <span className={styles.valueText}>{displayValue(currentTenant?.slug)}</span>
          </div>
          <div className={styles.valueItem}>
            <span className={styles.valueLabel}>{t("generic.email")}</span>
            <span className={styles.valueText}>{displayValue(currentTenant?.email)}</span>
          </div>
          <div className={styles.valueItem}>
            <span className={styles.valueLabel}>{t("generic.phone")}</span>
            <span className={styles.valueText}>{displayValue(currentTenant?.phone)}</span>
          </div>
        </div>

        <div className={styles.footer}>
          <Button appearance="secondary" onClick={() => refetch()} disabled={isBusy}>
            {t("generic.refresh")}
          </Button>
          <Button
            {...restoreFocusSourceAttributes}
            appearance="primary"
            onClick={() => setIsEditorOpen(true)}
            disabled={isBusy || !currentTenant}
          >
            {t("generic.edit")}
          </Button>
        </div>
      </div>

      <TenantEditor
        currentTenant={currentTenant}
        isOpen={isEditorOpen}
        setIsOpen={setIsEditorOpen}
        restoreFocusSourceAttributes={restoreFocusSourceAttributes}
      />

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Spinner size="tiny" />
          <span>{t("generic.loading")}</span>
        </div>
      )}
    </div>
  );
}

export default Tenant;
